import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { siteData } from '../data/mock';

const AnalyticsSection = () => {
  const data = siteData.analytics;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const total = data.cards.length;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goLeft = () => {
    if (activeIndex > 0) setActiveIndex((prev) => prev - 1);
  };
  const goRight = () => {
    if (activeIndex < total - 1) setActiveIndex((prev) => prev + 1);
  };

  const getCardStyle = (index) => {
    const diff = index - activeIndex;

    if (diff === 0) {
      return {
        transform: 'translateX(0) scale(1) rotate(0deg)',
        opacity: 1,
        zIndex: 3,
        border: '1.5px solid #FF98BF',
      };
    }
    if (diff === -1) {
      return {
        transform: 'translateX(-70%) scale(0.88) rotate(-2deg)',
        opacity: 0.45,
        zIndex: 1,
        border: '1px solid #c0c5ce',
      };
    }
    if (diff === 1) {
      return {
        transform: 'translateX(70%) scale(0.88) rotate(2deg)',
        opacity: 0.45,
        zIndex: 1,
        border: '1px solid #c0c5ce',
      };
    }
    // Hidden cards (further away)
    return {
      transform: `translateX(${diff > 0 ? '140%' : '-140%'}) scale(0.8)`,
      opacity: 0,
      zIndex: 0,
      border: '1px solid #c0c5ce',
    };
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      style={{
        background: '#fafafa',
        padding: 'clamp(44px, 7vw, 60px) var(--page-gutter) clamp(56px, 9vw, 80px)',
        overflow: 'hidden',
      }}
    >
      {/* Header: sticker + heading */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          marginBottom: '48px',
          paddingLeft: '0px',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}
      >
        {/* Pink tilted sticker — left-aligned, sits on top of "per video" */}
        <div style={{ position: 'relative', marginBottom: '-8px', zIndex: 2 }}>
          <span
            style={{
              display: 'inline-block',
              fontFamily: 'Coolvetica, sans-serif',
              fontSize: '28px',
              fontWeight: 400,
              color: '#fff',
              background: '#FF98BF',
              borderRadius: '6px',
              padding: '8px 22px',
              transform: 'rotate(-4deg)',
              letterSpacing: '-0.02em',
              boxShadow: '0 2px 8px rgba(255,152,191,0.2)',
            }}
          >
            {data.badge}
          </span>
        </div>
        <h2
          style={{
            fontFamily: 'Coolvetica, sans-serif',
            fontSize: '40px',
            fontWeight: 400,
            color: '#000',
            letterSpacing: '-0.025em',
            lineHeight: '44px',
          }}
        >
          {data.heading}
        </h2>
      </div>

      {/* Carousel area */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '380px',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.6s ease 0.2s',
        }}
      >
        {/* Left arrow button */}
        <button
          onClick={goLeft}
          className="nav-btn-macos"
          style={{
            position: 'absolute',
            left: '20px',
            zIndex: 10,
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: '1px solid #c0c5ce',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: activeIndex > 0 ? 'pointer' : 'default',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            opacity: activeIndex > 0 ? 1 : 0.3,
          }}
          aria-label="Previous card"
          disabled={activeIndex === 0}
        >
          <ChevronLeft size={20} color="#333" />
        </button>

        {/* Cards container */}
        <div
          style={{
            position: 'relative',
            width: '400px',
            height: '360px',
          }}
        >
          {data.cards.map((card, i) => {
            const cardStyle = getCardStyle(i);
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: '12px',
                  padding: 'clamp(22px, 5vw, 36px) clamp(18px, 4.6vw, 32px)',
                  background: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition:
                    'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.5s cubic-bezier(0.25,0.46,0.45,0.94)',
                  ...cardStyle,
                }}
                onClick={() => setActiveIndex(i)}
              >
                {/* Card number */}
                <span
                  style={{
                    fontFamily: 'Coolvetica, sans-serif',
                    fontSize: '28px',
                    color: '#FF98BF',
                    letterSpacing: '-0.025em',
                    lineHeight: '32px',
                    marginBottom: '24px',
                  }}
                >
                  {card.number}
                </span>

                {/* Card title */}
                <h3
                  style={{
                    fontFamily: 'Coolvetica, sans-serif',
                    fontSize: '32px',
                    fontWeight: 400,
                    color: '#000',
                    letterSpacing: '-0.025em',
                    lineHeight: '36px',
                    marginBottom: '16px',
                    textAlign: 'center',
                  }}
                >
                  {card.title}
                </h3>

                {/* Card body */}
                <p
                  style={{
                    fontFamily: 'InterVariable, sans-serif',
                    fontSize: '18px',
                    fontWeight: 400,
                    fontStyle: 'italic',
                    color: '#000',
                    lineHeight: '24px',
                    textAlign: 'center',
                    marginTop: 'auto',
                  }}
                >
                  {card.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right arrow button */}
        <button
          onClick={goRight}
          className="nav-btn-macos"
          style={{
            position: 'absolute',
            right: '20px',
            zIndex: 10,
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: '1px solid #c0c5ce',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: activeIndex < total - 1 ? 'pointer' : 'default',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            opacity: activeIndex < total - 1 ? 1 : 0.3,
          }}
          aria-label="Next card"
          disabled={activeIndex === total - 1}
        >
          <ChevronRight size={20} color="#333" />
        </button>
      </div>

      {/* Dot indicators */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginTop: '24px',
        }}
      >
        {data.cards.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            style={{
              width: activeIndex === i ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: activeIndex === i ? '#FF98BF' : '#c0c5ce',
              border: 'none',
              cursor: 'pointer',
              transition: 'width 0.3s ease, background 0.3s ease',
              padding: 0,
            }}
            aria-label={`Go to card ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default AnalyticsSection;
